import { DialogActions } from "@mui/material";
import { LoginFnError } from "apollo/mutations/login";
import { useEnvContext } from "context/EnvContext";
import {
  APP_CAREPROVIDER,
  APP_CLINIC,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_PENDING,
  RESPONSIVE_BREAKPOINT_MOBILE,
} from "core/consts";
import { getConfig } from "core/model/config";
import { LOCAL_STORAGE_KEYS } from "core/model/localStorage";
import { isDemo, isPreprod } from "core/model/utils/featureFlags";
import { getTopLevelDomain, getUrlEnvSuffix } from "core/model/utils/urls";
import { Env, QueryProgress } from "core/types";
import { millisecondsToMinutes } from "date-fns";
import { Banner } from "ds/ui";
import { FlagFab } from "ds_legacy/components/FlagFab/Fab";
import { DeFlagIcon } from "ds_legacy/components/FlagIcon";
import LinkV2 from "ds_legacy/components/LinkV2";
import RSButton from "ds_legacy/components/RSButton";
import { RecareLogoOnly } from "ds_legacy/components/RecareLogo";
import { SpinnerPage } from "ds_legacy/components/Spinner";
import { TextInputField } from "ds_legacy/components/TextInputField";
import {
  BORDER_COLOR,
  ICON_DARK,
  ICON_GREY,
  WHITE,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import {
  border,
  dp,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import { SHADOW_DROP_SMALL } from "ds_legacy/materials/shadows";
import {
  Body,
  FONT_SIZE_12,
  FONT_SIZE_16,
  LINE_HEIGHT_16,
  Link,
  Subheading,
} from "ds_legacy/materials/typography";
import LoginSuggestions from "dsl/ecosystems/CareproviderOnboardingLoginPage/LoginSuggestions";
import { ChevronLeftIcon, LockIcon, MailIcon } from "lucide-react";
import { usePanda } from "pandatracker-client";
import React, {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import Translations from "translations/types";

export type EnhancedLoginError = LoginFnError & {
  remaining_time: number;
  status: number;
};

export const Overlay = styled.div`
  display: flex;
  background: radial-gradient(#40404b, #111118) rgba(34, 34, 40, 0.94);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  justify-content: center;
`;

export const LockCenter = styled.div`
  display: flex;
  align-items: center;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  height: ${sizing(9)};
  padding: ${padding(0, 3)};
  border-top-right-radius: ${dp(5)};
  border-top-left-radius: ${dp(5)};
  background: ${(props) => (props.theme as any).palette.primary.main};
`;

export const HeaderWithIcon = styled.div`
  display: flex;
  align-items: center;
  height: ${sizing(9)};
  padding: ${padding(0, 3, 0, 1)};
  border-top-right-radius: ${dp(5)};
  border-top-left-radius: ${dp(5)};
  background: ${(props) => (props.theme as any).palette.primary.main};
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  width: ${sizing(50)};
  height: ${sizing(4)};
  border-top: ${border({ color: BORDER_COLOR })};
  justify-content: center;
  font-size: ${FONT_SIZE_12};
`;

export const PasswordResetPageLink = styled(Subheading)`
  font-size: ${FONT_SIZE_12};
  margin: ${margin(-0.5, 2)};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${sizing(50)};
`;

export const FooterWrapper = styled.div`
  display: flex;
  padding: ${padding(2, 0, 0, 0)};
  flex-grow: 1;
  align-items: flex-end;
  justify-content: center;
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: ${margin(2, 3)};
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const Widget = styled.div<{ height?: string; minHeight?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${sizing(50)};
  margin: 0 auto;
  border-radius: ${dp(5)};
  height: ${(props) => props.height || dp(500)};
  max-height: 100vh;
  background: white;
  box-shadow: 0 0 40px 4px #111118;
  @media screen and (max-width: ${dp(RESPONSIVE_BREAKPOINT_MOBILE)}) {
    & {
      height: 100vh;
      width: 100vw;
    }
  }
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
`;

export const OnboardingWidget = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${sizing(50)};
  margin: 0 auto;
  border-radius: ${dp(5)};
  height: ${dp(500)};
  max-height: 100vh;
  background: white;
  box-shadow: ${SHADOW_DROP_SMALL};
  @media screen and (max-width: ${dp(RESPONSIVE_BREAKPOINT_MOBILE)}) {
    & {
      height: 100vh;
      width: 100vw;
    }
  }
`;

function getTranslationCompositionLink(translation: string, link: string) {
  return (
    <TranslationComposition translations={translation} withOptions>
      {([before, selectLink, after]) => (
        <span>
          {before}
          <LinkV2 href={link}>{selectLink}</LinkV2>
          {after}
        </span>
      )}
    </TranslationComposition>
  );
}

export function getLoginError(err: LoginFnError): EnhancedLoginError {
  const error: EnhancedLoginError = {
    remaining_time: 0,
    status: -1,
    ...err,
  };

  try {
    if (err.message != null && typeof err.message === "string") {
      const json = JSON.parse(err.message);

      error.status = json.status;
      if (json.body.remaining_time) {
        error.remaining_time = json.body.remaining_time;
      }
    }
  } catch (e) {
    console.warn("Could not parse error message", e);
  }

  return error;
}

export function getLoginFailureReason({
  challenge,
  rawError,
  remaining_time,
  status,
  token,
  translations,
}: {
  challenge: string | undefined;
  rawError: string;
  remaining_time: number;
  status: number;
  token: string | undefined;
  translations: Translations;
}) {
  let reason = "";

  const rawErrorLower = rawError.toLowerCase();

  switch (status) {
    case 429:
      reason =
        translations.login.biometricfailedAlert.falsePasswordLimitReached({
          timer: String(millisecondsToMinutes(remaining_time) + 1),
        });
      break;

    default:
      if (rawError.includes("connected with unauthorized IP")) {
        reason = "Please connect to the VPN before login";
      } else if (rawError.includes("no private key")) {
        reason = translations.auctionResponse.accessDeniedError;
      } else if (rawError === translations.login.toastInactiveProvider) {
        reason = translations.login.toastInactiveProvider;
      } else if (rawError === "cleandata in progress") {
        reason = "cleandata in progress";
      } else {
        if (challenge) {
          const datetimestring =
            window.sessionStorage.getItem(
              LOCAL_STORAGE_KEYS.CHALLENGE_DATETIME,
            ) ?? "";
          reason =
            translations.login.challengeScreen.verificationInvalidTimestamp({
              datetimestring,
            });
        } else if (token) {
          reason = "Invalid token";
        } else if (rawErrorLower.includes("seald")) {
          console.error("[getLoginFailureReason]:", rawError);
          reason = rawError;
        } else if (rawErrorLower.includes("no such user")) {
          reason = translations.login.wrongEmailPassword;
        } else if (rawErrorLower.includes("bad password")) {
          reason = translations.login.wrongEmailPassword;
        } else {
          console.error("[getLoginFailureReason]:", rawError);
          reason = translations.general.error;
        }
      }
      break;
  }

  return reason;
}

export function useAutofilled(refs: Array<React.MutableRefObject<ToType>>) {
  const [isAutofilled, setIsAutofilled] = useState(false);

  useLayoutEffect(() => {
    if (!isAutofilled && refs.length > 0 && refs[0].current != null) {
      const timeout = setTimeout(() => {
        let accumulator = true;
        refs.forEach((r) => {
          try {
            if (r.current)
              accumulator =
                accumulator && r.current.matches(":-webkit-autofill");
          } catch (_error) {
            // no ':-webkit-autofill' selector
            accumulator = false;
          }
        });
        setIsAutofilled(accumulator);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, []);
  return isAutofilled;
}

export const getTitleAndUrl = (
  app: number,
  env: string,
  translations: Translations,
) => {
  const { domain } = getConfig(env as Env);

  switch (app) {
    case APP_CLINIC:
      return {
        redirectTitle: translations.login.clinic.changeToReceiver,
        redirectUrl: `https://partner${getUrlEnvSuffix(
          env,
        )}.${domain}/careprovider/auth`,
        showEnvironmentWarning: isDemo || isPreprod,
        title: isDemo
          ? translations.login.clinic.loginTitleDemo.toUpperCase()
          : isPreprod
          ? translations.login.clinic.loginTitlePreprod.toUpperCase()
          : translations.login.clinic.loginTitle.toUpperCase(),
      };

    case APP_CAREPROVIDER:
      return {
        redirectTitle: translations.login.care.changeToSender,
        redirectUrl: `https://app${getUrlEnvSuffix(env)}.${domain}/app/auth`,
        showEnvironmentWarning: false,
        title: translations.login.care.loginTitle.toUpperCase(),
      };

    default:
      return {
        redirectTitle: "",
        redirectUrl: "",
        showEnvironmentWarning: false,
        title: translations.login.loginTitle.toUpperCase(),
      };
  }
};

const SWITCHER_FLAGS: {
  [key: string]: React.ComponentType<{ isActive?: boolean }>;
} = { de: DeFlagIcon };

const getCountrySwitcherUrl = ({
  currentApp,
  urlSuffix,
}: {
  currentApp: string;
  urlSuffix: string;
}) => `https://${currentApp}${urlSuffix}.recaresolutions.com}`;

const CountrySwitcher = ({ app, env }: { app: number; env: string }) => {
  const currentApp = app === APP_CAREPROVIDER ? "partner" : "app";
  const urlSuffix = getUrlEnvSuffix(env);
  const initialFlagState = getTopLevelDomain().toLowerCase();
  const containerStyle: CSSProperties = {
    bottom: sizing(2),
    position: "fixed",
    right: sizing(3),
    zIndex: 100,
  };
  return (
    <FlagFab
      containerStyleOverride={containerStyle}
      flags={SWITCHER_FLAGS}
      flagState={initialFlagState}
      onFlagChange={() => {
        const URL = getCountrySwitcherUrl({ currentApp, urlSuffix });
        window.open(URL);
      }}
    />
  );
};

export function LoginProgressPending({
  id,
  title,
}: {
  id: string;
  title: string;
}) {
  usePanda("LoginPageLoading");
  const translations = useTranslations();

  return (
    <>
      <Header>
        <HeaderWrapper>
          <Subheading light>{title}</Subheading>
          <RecareLogoOnly />
        </HeaderWrapper>
      </Header>
      <VerticalLayout height={sizing(12)} margin={margin(9, 0)}>
        <SpinnerPage id={id} />
      </VerticalLayout>
      <VerticalLayout margin={margin(0, 4)}>
        <Subheading bold>
          {translations.login.loading.settingEnvironment}
        </Subheading>
        <Body>{translations.login.loading.dontLeavePage}</Body>
      </VerticalLayout>
    </>
  );
}

export function LoginModule({
  email,
  error,
  isPasswordRequired,
  onLogin,
  onLoginQueryProgress,
  onPasswordResetPage,
  password,
  setEmail,
  setPassword,
  status,
}: {
  email: string;
  error: string | null;
  isPasswordRequired: boolean;
  onLogin: () => void;
  onLoginQueryProgress: QueryProgress;
  onPasswordResetPage: () => void;
  password: string;
  setEmail: Dispatch<SetStateAction<string>>;
  setPassword: Dispatch<SetStateAction<string>>;
  status: string;
}) {
  const translations = useTranslations();
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const isAutofilled = useAutofilled([emailRef, passwordRef]);
  const canSubmit =
    onLoginQueryProgress !== QUERY_PROGRESS_PENDING &&
    (isAutofilled ||
      (isPasswordRequired && password && email) ||
      (!isPasswordRequired && email));

  const { app } = useEnvContext();
  const { env } = useEnvContext();

  const { redirectTitle, redirectUrl, showEnvironmentWarning, title } =
    getTitleAndUrl(app, env, translations);

  const { token } = useParams();

  useEffect(() => {
    if (token) onLogin();
  }, [token]);

  if (onLoginQueryProgress === QUERY_PROGRESS_PENDING)
    return <LoginProgressPending id="login-page" title={title} />;

  return (
    <>
      <Header>
        <HeaderWrapper>
          <Subheading bold light>
            {title}
          </Subheading>
          <RecareLogoOnly />
        </HeaderWrapper>
      </Header>

      <StyledForm
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onLogin();
        }}
      >
        <LoginSuggestions />
        <VerticalLayout stretched style={{ marginTop: sizing(3) }}>
          {showEnvironmentWarning && (
            <div className="mb-2 w-full">
              <Banner
                color="warning"
                message={getTranslationCompositionLink(
                  translations.login.clinic.bannerDemo,
                  "https://app.recaresolutions.com",
                )}
                withIcon={false}
              />
            </div>
          )}
          <TextInputField
            autoComplete="on"
            autoFocus
            elementName="email"
            inputRef={emailRef}
            label={translations.login.emailAddress}
            required
            textInputType="email"
            validateOverride=" "
            validation={onLoginQueryProgress !== QUERY_PROGRESS_FAILED}
            value={email}
            width="90%"
            onChange={setEmail}
          />
          {isPasswordRequired ? (
            <VerticalLayout>
              <HorizontalLayout stretched>
                <TextInputField
                  autoComplete="current-password"
                  elementName="password"
                  inputRef={passwordRef}
                  label={translations.login.password}
                  required
                  textInputType="password"
                  validateOverride=" "
                  validation={onLoginQueryProgress !== QUERY_PROGRESS_FAILED}
                  value={password}
                  width="90%"
                  onChange={setPassword}
                />
              </HorizontalLayout>
              <PasswordResetPageLink bold primary onClick={onPasswordResetPage}>
                {translations.login.forgotYourPassword}
              </PasswordResetPageLink>
            </VerticalLayout>
          ) : (
            <HorizontalLayout stretched>
              <Body
                display="flex"
                flex="row nowrap"
                margin={margin(2, 2, 6.25, 2)}
              >
                <LockIcon
                  size={FONT_SIZE_16}
                  style={{
                    color: ICON_DARK,
                    fontSize: FONT_SIZE_16,
                    margin: margin(0, 1, -0.5, 0),
                  }}
                />
                {translations.login.ssoEnabled}
              </Body>
            </HorizontalLayout>
          )}
        </VerticalLayout>
        {status === "login" && error != null && <Body error>{error}</Body>}
        <>
          <DialogActions
            sx={{ justifyContent: "center", margin: margin(1, 0) }}
          >
            <RSButton
              className="login"
              color="primary"
              disabled={!canSubmit}
              id="login"
              loading={onLoginQueryProgress}
              style={{ margin: 0, width: dp(220) }}
              type="submit"
              variant="contained"
              onClick={() => {}}
            >
              {translations.login.login}
            </RSButton>
          </DialogActions>
          {redirectTitle && (
            <BottomWrapper>
              <Body
                fontSize={FONT_SIZE_12}
                lineHeight={LINE_HEIGHT_16}
                secondary
              >
                {translations.login.haveAnAccountYet}&nbsp;
                <Link
                  href={translations.login.contactUrl}
                  rel="noopener noreferrer"
                  secondary
                  target="_blank"
                >
                  {translations.login.contactUs}
                </Link>
              </Body>

              <Body
                fontSize={FONT_SIZE_12}
                lineHeight={LINE_HEIGHT_16}
                secondary
              >
                {redirectTitle}&nbsp;
                <Link
                  aria-label={translations.login.clinic.clickHere}
                  href={redirectUrl}
                  rel="noopener noreferrer"
                  secondary
                  target="_blank"
                >
                  {translations.login.clinic.clickHere}
                </Link>
              </Body>
            </BottomWrapper>
          )}
        </>
      </StyledForm>
      <CountrySwitcher app={app} env={env} />
    </>
  );
}

export function ChallengeModule({
  challenge,
  error,
  onLogin,
  onLoginPage,
  onLoginQueryProgress,
  setChallenge,
  status,
}: {
  challenge: string | undefined;
  error: string | null;
  onLogin: () => void;
  onLoginPage: () => void;
  onLoginQueryProgress: QueryProgress;
  setChallenge: Dispatch<SetStateAction<string | undefined>>;
  status: string;
}) {
  const translations = useTranslations();
  const canSubmit =
    onLoginQueryProgress !== QUERY_PROGRESS_PENDING && challenge;

  const { app } = useEnvContext();
  const { env } = useEnvContext();

  const title = translations.login.challengeScreen.title;
  const datetimestring =
    window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.CHALLENGE_DATETIME) ?? "";

  if (onLoginQueryProgress === QUERY_PROGRESS_PENDING)
    return <LoginProgressPending id="challenge-page" title={title} />;

  return (
    <>
      <HeaderWithIcon>
        <ChevronLeftIcon
          style={{ color: WHITE, cursor: "pointer" }}
          onClick={onLoginPage}
        />
        <HeaderWrapper>
          <Subheading bold light>
            {title}
          </Subheading>
          <RecareLogoOnly />
        </HeaderWrapper>
      </HeaderWithIcon>

      <StyledForm
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onLogin();
        }}
      >
        <VerticalLayout stretched style={{ marginTop: sizing(3) }}>
          <VerticalLayout aligned margin={margin(4, 0)}>
            <MailIcon
              size={FONT_SIZE_16}
              style={{ color: ICON_GREY, margin: margin(0, 0, 2, 0) }}
            />
            <Body>{translations.login.challengeScreen.infoText} </Body>
          </VerticalLayout>
          <TextInputField
            autoFocus
            elementName="challenge"
            label={translations.login.challengeScreen.placeholder}
            required
            textInputType="challenge"
            validateOverride=" "
            validation={onLoginQueryProgress !== QUERY_PROGRESS_FAILED}
            value={challenge ?? ""}
            width="90%"
            onChange={setChallenge}
          />
          {status === "login" && error != null && <Body error>{error}</Body>}
          {!error && (
            <Body>
              {translations.login.challengeScreen.infoTextTimestamp({
                datetimestring,
              })}
            </Body>
          )}
        </VerticalLayout>
        <DialogActions
          sx={{ justifyContent: "center", margin: margin(2, 0, 1, 0) }}
        >
          <RSButton
            className="login"
            color="primary"
            disabled={!canSubmit}
            id="login"
            loading={onLoginQueryProgress}
            style={{ margin: 0, width: dp(220) }}
            type="submit"
            variant="contained"
            onClick={() => {}}
          >
            {translations.login.login}
          </RSButton>
        </DialogActions>
      </StyledForm>
      <CountrySwitcher app={app} env={env} />
    </>
  );
}
